import React, { useCallback, useMemo, useRef, useState } from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';
import classnames from 'classnames';
import _ from 'lodash';
import moment from 'moment-timezone';
import { TAvailability, useAvailabilitiesCount, useAvailabilityUpdateOneBook } from 'ui-model';

import { useCurrentLanguage, useCurrentUser } from '@lib/hooks';
import { isAnyMobile } from '@lib/services/navigatorService';
import { getRedirectConfig, getTranslation } from '@lib/services/patientFormsService';

import { Button, ExpansionPanelProviders, Typography } from 'components/v2';
import { generateMessageTemplateKey, useSettingSchedulerDates } from 'hooks';

import { ChatContextProps, useChatContext } from '../../contexts/ChatContext';
import useSettingsLumaBot from '../../hooks/useSettingsLumaBot';
import { getLumaBotErrorMessageHandler } from '../../utils';
import ChatMessage, { ChatMessageProps } from '../Messages/ChatMessage';
import { TMessageWrapper } from '../Messages/MessageWrapper';

import QueryListAvailabilitiesGrouped from './QueryListAvailabilitiesGrouped';
import { getLumaBotItemSchedulerEmpty } from './utils';

const isMobile = isAnyMobile();

const styles = ({ spacing: { unit }, palette }: Theme) =>
  createStyles({
    showMoreWrapper: {
      display: 'flex',
      position: 'fixed',
      top: 56,
      left: 0,
      right: 0,
      bottom: 10,
      zIndex: 100,
      overflowY: 'hidden',
      borderRadius: 6,
    },
    showMoreWrapperDesktop: {
      top: 80,
      left: 24,
      right: 24,
      bottom: 34,
    },
    showMore: {
      flex: 1,
      background: palette.common.white,
      padding: unit * 3,
      overflowY: 'auto',
      animation: 'slideInLeft 1s',
    },
    expansionPanel: {
      marginBottom: unit * 2,
    },
    '@keyframes slideInLeft': {
      '0%': {
        transform: 'translateX(200px)',
      },
      '100%': {
        transform: 'translateX(0)',
      },
    },
    schedulerButton: {
      marginBottom: unit,
      backgroundColor: palette.common.white,
      '&:hover': {
        backgroundColor: fade(palette.common.white, 0.8),
      },
    },
  });

export interface MessageSchedulerProps extends Omit<ChatMessageProps, 'classes'>, WithStyles<typeof styles> {
  message: TMessageWrapper;
  messageTemplate: string;
  onSubmit: ChatContextProps['pushAnswer'];
}

const MessageScheduler: React.FC<MessageSchedulerProps> = ({
  classes,
  message,
  messageTemplate,
  onSubmit,
  ...rest
}) => {
  const { flowId, flowTemplates, mode, setMode } = useChatContext();
  const [{ startDate, endDate }, setDateRange] = useState(useSettingSchedulerDates());
  const language = useCurrentLanguage();
  const currentUser = useCurrentUser();
  const currentUserId = currentUser?.id || '';
  const $isWidgetUser = currentUser?.$isWidgetUser;
  const schedulerItem = _.get(message, 'metadata.item');

  // NOTE: `redirectConfig` set on `useLumaBotActions#getNextItem`
  // taking into account the selected answer instead of the whole item
  // NOTE: keeping fallback for safety
  // @ts-ignore
  const redirectConfig = schedulerItem?.redirectConfig || getRedirectConfig(message.metadata.item as any);

  const { isHideProviderDetails } = useSettingsLumaBot();
  const showFilters = !!redirectConfig?.properties?.showFilters && !isHideProviderDetails;

  const filters = useMemo(() => {
    const facility = redirectConfig?.properties?.params?.facility;
    const provider = _.compact(redirectConfig?.properties?.params?.provider) as string[];
    const appointmentType = redirectConfig?.properties?.params?.appointmentType;
    const includeNullApptTypes = appointmentType ? !(appointmentType.length > 0) : true;
    return { facility, provider, appointmentType, includeNullApptTypes };
  }, [redirectConfig]);

  const sanitizedFacilityFilter = _.compact(filters.facility).join(',');

  const divRef = useRef<HTMLDivElement>(null);
  const [isSchedulerDisabled, setSchedulerDisabled] = useState(false);
  const availabilityUpdateOneBookMutation = useAvailabilityUpdateOneBook();

  const [providerFilter, setProviderFilter] = useState<string[]>(() => filters.provider);

  const [shouldCountAvailabilities, setCountAvailabilities] = useState(true);
  const [shouldRenderScheduler, setRenderScheduler] = useState(true);
  const { isLoading: isAvailabilitiesCountLoading } = useAvailabilitiesCount(
    {
      status: 'available',
      dateMin: startDate ? startDate.format() : '',
      dateMax: endDate ? endDate.format() : '',
      ...filters,
    },
    {
      // Run the count query just once in order to verify if there are any availabilities
      enabled: shouldCountAvailabilities,
      refetchOnWindowFocus: false,
      onSuccess: (data: number[]) => {
        setCountAvailabilities(false);
        if (data.length === 1 && data[0] === 0) {
          setRenderScheduler(false);
          const item = getLumaBotItemSchedulerEmpty(flowTemplates, flowId);
          onSubmit(
            // @ts-ignore => forceName should be a temporary fix. Should get rid of this when refactor.
            { text: 'force', type: 'inbound', metadata: { item, forceName: true } },
            { skipNextItem: true, customDelay: 1000 },
          );
        }
      },
    },
  );

  const handleBookAppointment = useCallback(
    (availability: TAvailability) => {
      if (!availability) return;

      const patientFormId = message?.metadata?.flowId || '';
      const patientFormTemplateId = message?.metadata?.flowTemplateId || '';

      availabilityUpdateOneBookMutation.mutate(
        {
          _id: availability._id,
          filters: availability.filters,
          patient: $isWidgetUser ? '' : currentUserId,
          patientForm: patientFormId,
          patientFormTemplate: patientFormTemplateId,
          source: availability.source,
        },
        {
          onSettled: () => {
            setSchedulerDisabled(true);
          },
          onSuccess: async (appointment) => {
            const availabilitySelectdLabel = `${moment(availability!.date).format('MMM D, YYYY - LT')}\n${
              availability!.provider.name
            }`;
            // Trigger user's response based on availability selected
            // TODO: store the selected appointment inside the flow (patientForm) as an answer
            onSubmit({ text: availabilitySelectdLabel, type: 'outbound' }, { skipNextItem: true });

            // Trigger lumabot's text response
            const pendingText = generateMessageTemplateKey('patientApptManagementPending', 'web');
            // how many characters users can read per millisecond:
            const millisecondsMultiplier = 20;
            const customPendingDelay = availabilitySelectdLabel.length * millisecondsMultiplier;
            const customStatusDelay = pendingText.length * millisecondsMultiplier;

            onSubmit({ text: pendingText, type: 'inbound' }, { customDelay: customPendingDelay, skipNextItem: true });

            // TODO: would be better to have a `pushMessagesList` function to handle awaiting times between multiple submits like this
            await new Promise((resolve) => setTimeout(resolve, customPendingDelay));

            onSubmit(
              {
                type: 'inbound',
                metadata: {
                  appointment: {
                    ...appointment,
                    facility: availability.facility,
                    provider: availability.provider,
                    appointmentType: availability.appointmentType,
                  },
                  item: { ...message.metadata.item, ...redirectConfig, type: 'book_status_component' },
                },
              },
              { customDelay: customStatusDelay },
            );
          },
          onError: () => {
            const item = getLumaBotErrorMessageHandler(flowTemplates, schedulerItem?.type);
            onSubmit({ text: 'force', type: 'inbound', metadata: { item } }, { skipNextItem: true });
          },
        },
      );
    },
    [
      availabilityUpdateOneBookMutation,
      onSubmit,
      message,
      currentUserId,
      $isWidgetUser,
      flowTemplates,
      redirectConfig,
      schedulerItem,
    ],
  );

  if (isAvailabilitiesCountLoading) return <ChatMessage message={message} {...rest} variant="typing" />;

  if (!shouldRenderScheduler) return null;

  return (
    <>
      <ChatMessage message={message} {...rest}>
        {messageTemplate && <Typography dangerouslySetInnerHTML={{ __html: messageTemplate }} />}
        <Button
          className={classes.schedulerButton}
          disabled={isSchedulerDisabled || _.get(message, 'metadata.nextMessage')}
          onClick={() => setMode('blocked')}
          variant="outlined"
          extraPadding
          extraRounded
          fullWidth
        >
          {getTranslation(schedulerItem, language, 'schedulerButton')}
        </Button>
        <div ref={divRef} />
      </ChatMessage>
      {mode === 'blocked' && (
        <div className={classnames(classes.showMoreWrapper, { [classes.showMoreWrapperDesktop]: !isMobile })}>
          <div className={classes.showMore}>
            {/* Filter by provider/s */}
            {showFilters && (
              <ExpansionPanelProviders
                className={classes.expansionPanel}
                value={providerFilter}
                preFilteredProviders={filters.provider}
                onChange={(filteredProviders) => {
                  setProviderFilter(filteredProviders);
                }}
              />
            )}
            {/* List extended */}
            <QueryListAvailabilitiesGrouped
              params={{
                ...filters,
                date: !!startDate && !!endDate ? [`>${startDate?.format()}`, `<${endDate?.format()}`] : undefined,
                provider: _.isEmpty(providerFilter) ? filters.provider.join(',') : providerFilter.join(','),
                facility: sanitizedFacilityFilter,
              }}
              dateRange={{ startDate, endDate }}
              setDateRange={setDateRange}
              onChange={(value) => {
                setMode('default');
                setTimeout(() => {
                  handleBookAppointment(value);
                });
              }}
              isMobile
            />
          </div>
        </div>
      )}
    </>
  );
};

export default withStyles(styles, { name: 'MessageScheduler' })(MessageScheduler);
